import PropTypes from 'prop-types';
import Chatbot from 'react-chatbot-kit';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import 'react-chatbot-kit/build/main.css';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import config from './react-chatbot-kit/config';
import MessageParser from './react-chatbot-kit/MessageParser';
import ActionProvider from './react-chatbot-kit/ActionProvider';
import { ChatConsumer, ChatProvider } from './context';
import { useChatContext } from './context/chat-context';
import './chatbot.css';

export const Chat = ({isMinimized, setIsMinimized, showChatButton, headerText = "tool", onClose}) => {

  const { 
    isSending, 
    createTokenCallback, 
    setChatActive,
    createTokenLoading, 
    type,
    templateHistory,
    intermediateProcessMessage
  } = useChatContext();

  const [sourceCodeDialog, setSourceCodeDialog] = useState({ 
    open: false, 
    code: '', 
    timestamp: null 
  });
  const hasRequestedTokenRef = useRef(false);

  const validate = useCallback((message) => {

    if (message === "") {
      return false
    }

    if (isSending) {
      return false;
    }
    return true
  }, [isSending])


  useEffect(() => {
    if (!isMinimized && !hasRequestedTokenRef.current) {
      hasRequestedTokenRef.current = true;
      createTokenCallback();
      return;
    }
    if (isMinimized) {
      hasRequestedTokenRef.current = false;
    }
  }, [isMinimized, createTokenCallback])

  // 欄が開いている間だけ WS を張る（chat-provider.js 参照）。
  useEffect(() => {
    setChatActive(!isMinimized);
    return () => setChatActive(false);
  }, [isMinimized, setChatActive])

  let placeholderText = "メッセージを入力してください…"
  if (type === "template-simple" || type === "template-simple-v2") {
    placeholderText = "メッセージを入力してください… (/create_code: to create code)"
  }

  const isTemplateChat = type === "template-simple" || type === "template-simple-v2";

  const handleHistoryItemClick = useCallback((historyItem) => {
    setSourceCodeDialog({
      open: true,
      code: historyItem.code || '',
      timestamp: historyItem.timestamp
    });
  }, []);

  const handleCloseSourceDialog = useCallback(() => {
    setSourceCodeDialog({ open: false, code: '', timestamp: null });
  }, []);

  const formatTimestamp = useCallback((timestamp) => 
    new Date(timestamp).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }), []);

  return (
    <Box className="chatbot-wrapper" sx={{ m: 0, p: 0 }}>
      <Stack>
        {showChatButton && (
          <Button
            variant="contained"
            color="primary"
            sx={{
              borderRadius: '20px',
              mb: 1,
              boxShadow: 2,
              px: 3,
              py: 1,
              minWidth: '100px',
              fontWeight: 'bold',
              '&:hover': {
                boxShadow: 4,
              },
            }}
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? 'チャット' : '閉じる'}
          </Button>
        )}
        
      </Stack>

      <Stack
        className="resizable-container"
        sx={{
          maxWidth: '100%',
          m: 0,
          p: 0,
          display: isMinimized ? 'none' : 'block',
        }}
      >
        {createTokenLoading ? (
          <Container
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Stack>
              <p>Loading...</p>
            </Stack>
          </Container>
        ) : (
          <>
            <Chatbot
              config={config}
              messageParser={MessageParser}
              actionProvider={ActionProvider}
              validator={validate}
              headerText={headerText}
              placeholderText={placeholderText}
            />
            {/* Loading message styling */}
            {isSending && (
              <div className="loading-message-container">
                <div className="react-chatbot-kit-chat-bot-message-container">
                  <div className="react-chatbot-kit-chat-bot-avatar-container">
                    <svg width="90" height="90" viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="45" cy="45" r="44" fill="#0A39E9" stroke="#0A39E9" strokeWidth="2"/>
                      <mask id="mask0_2417_13" style={{maskType: 'alpha'}} maskUnits="userSpaceOnUse" x="12" y="12" width="66" height="66">
                      <rect x="12" y="12" width="66" height="66" fill="#D9D9D9"/>
                      </mask>
                      <g mask="url(#mask0_2417_13)">
                      <path d="M34.7888 67.2H52.3888C52.7888 67.2 53.0888 66.7 52.6888 66.4L35.0888 48.8C34.7888 48.5 34.2888 48.8 34.2888 49.1V66.7C34.2888 67 34.4888 67.2 34.7888 67.2Z" fill="white"/>
                      <path d="M54.9888 46H37.3888C36.9888 46 36.6888 46.5 37.0888 46.8L54.6888 64.4C54.9888 64.7 55.4888 64.4 55.4888 64.1V46.5C55.4888 46.2 55.2888 46 54.9888 46Z" fill="white"/>
                      <path d="M34.7888 43.2H52.3888C52.7888 43.2 53.0888 42.7 52.6888 42.4L35.0888 24.8C34.7888 24.5 34.2888 24.8 34.2888 25.1V42.7C34.2888 43 34.4888 43.2 34.7888 43.2Z" fill="white"/>
                      <path d="M54.9888 22H37.3888C36.9888 22 36.6888 22.5 37.0888 22.8L54.6888 40.4C54.9888 40.7 55.4888 40.4 55.4888 40.1V22.5C55.4888 22.2 55.2888 22 54.9888 22Z" fill="white"/>
                      <path d="M58.2888 25.1V42.7C58.2888 43 58.4888 43.2 58.7888 43.2H76.3888C76.7888 43.2 77.0888 42.7 76.6888 42.4L59.0888 24.8C58.7888 24.5 58.2888 24.8 58.2888 25.1Z" fill="white"/>
                      <path d="M31.4889 64V46.4C31.4889 46.1 31.2889 45.9 30.9889 45.9H13.4889C13.0889 45.9 12.7889 46.4 13.1889 46.7L30.7889 64.3C31.0889 64.6 31.5889 64.3 31.5889 64H31.4889Z" fill="white"/>
                      </g>
                    </svg>
                  </div>
                  <div className="loading-message-bubble">
                    <div className="chatbot-loader-container">
                      <div id="chatbot-loader">
                        <span id="chatbot-loader-dot1">.</span>
                        <span id="chatbot-loader-dot2">.</span>
                        <span id="chatbot-loader-dot3">.</span>
                      </div>
                    </div>
                    {/* Intermediate process status */}
                    {intermediateProcessMessage && (
                      <div style={{
                        marginTop: '8px',
                        fontSize: '12px',
                        color: '#666',
                        fontStyle: 'italic',
                        textAlign: 'center'
                      }}>
                        {intermediateProcessMessage}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}    
      </Stack>

      {/* Template History List - Below Chat */}
      {isTemplateChat && templateHistory.length > 0 && !isMinimized && (
        <Accordion sx={{ mt: 1 }}>
          <AccordionSummary
            expandIcon={<Typography>▼</Typography>}
            sx={{ 
              minHeight: 48,
              '& .MuiAccordionSummary-content': {
                margin: '8px 0',
              }
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontWeight="medium">
                テンプレート履歴
              </Typography>
              <Chip 
                label={`${templateHistory.length}件`} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <List dense sx={{ width: '100%' }}>
              {templateHistory.map((historyItem, index) => (
                <ListItemButton
                  key={`${historyItem.timestamp}-${index}`}
                  onClick={() => handleHistoryItemClick(historyItem)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight="medium">
                        履歴 {index + 1}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(historyItem.timestamp)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Source Code Dialog */}
      <Dialog
        open={sourceCodeDialog.open}
        onClose={handleCloseSourceDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">テンプレートソースコード</Typography>
            {sourceCodeDialog.timestamp && (
              <Typography variant="caption" color="text.secondary">
                {formatTimestamp(sourceCodeDialog.timestamp)}
              </Typography>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              backgroundColor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              height: '100%',
            }}
          >
            {sourceCodeDialog.code || 'コードがありません'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSourceDialog} variant="contained">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

Chat.propTypes = {
  isMinimized: PropTypes.bool,
  setIsMinimized: PropTypes.func,
  showChatButton: PropTypes.bool,
  headerText: PropTypes.string,
  onClose: PropTypes.func,
}

const CmsChatbot = forwardRef(({
                                 type,
                                 config_data,
                                 def_minimized = true,
                                 minimized,
                                 show_chat_button = true,
                                 headerText = "tool",
                                 setTemplateCode,
                                 getTemplateCode,
                                 getSiteConfigData,
                                 onClose,
                               }, ref) => {

  const [minimizedState, setIsMinimized] = useState(def_minimized);

  // minimized を渡されたら親の開閉に従う（MultiChatbot のように、欄の表示を
  // 親が持っている場合）。渡されなければ従来どおり自前の状態。
  const isMinimized = minimized ?? minimizedState;

  const getIsMinimized = () => isMinimized;

  useImperativeHandle(ref, () => ({
    setIsMinimized,
    getIsMinimized,
  }))

  return <ChatProvider type_str={type}
                       config_data={config_data}
                       set_template_code={setTemplateCode}
                       get_template_code={getTemplateCode}
                       get_site_config_data={getSiteConfigData}
  >
    <ChatConsumer>
      <Chat isMinimized={isMinimized}
            setIsMinimized={setIsMinimized}
            showChatButton={show_chat_button}
            headerText={headerText}
            onClose={onClose}
      />
    </ChatConsumer>
  </ChatProvider>
})

export default CmsChatbot

CmsChatbot.propTypes = {
  type: PropTypes.string.isRequired,
  config_data: PropTypes.object,
  def_minimized: PropTypes.bool,
  minimized: PropTypes.bool,
  show_chat_button: PropTypes.bool,
  headerText: PropTypes.string,
  setTemplateCode: PropTypes.func,
  getTemplateCode: PropTypes.func,
  onClose: PropTypes.func,
  getSiteConfigData: PropTypes.func,
}
